import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { 
  LoginResponse, 
  AudioAnalysisListResponse, 
  AudioAnalysis, 
  StatisticsResponse, 
  ApiResponse,
  AudioAnalysisFilters
} from '../types';

// Configuração base da API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor para adicionar token de autenticação
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('admin_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Interceptor para tratamento de respostas
    this.api.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        // Se token expirou, limpar localStorage e redirecionar para login
        if (error.response?.status === 401) {
          localStorage.removeItem('admin_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Autenticação
  async login(username: string, password: string): Promise<LoginResponse> {
    console.log('🌐 API Service - Fazendo requisição de login:', { 
      username, 
      password: password ? '***' : 'empty',
      baseURL: this.api.defaults.baseURL,
      url: '/auth/login' 
    });
    
    try {
      const response = await this.api.post('/auth/login', {
        username,
        password,
      });
      console.log('🌐 API Service - Resposta recebida:', response.data);
      return response.data;
    } catch (error: any) {
      console.log('🌐 API Service - Erro na requisição:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        config: error.config
      });
      throw error;
    }
  }

  async validateToken(): Promise<ApiResponse> {
    const response = await this.api.get('/auth/validate');
    // O backend retorna user diretamente, não em data
    // Vamos mapear para o formato esperado
    if (response.data.success && response.data.user) {
      return {
        success: response.data.success,
        message: response.data.message,
        data: response.data.user
      };
    }
    return response.data;
  }

  async logout(): Promise<ApiResponse> {
    const response = await this.api.post('/auth/logout');
    return response.data;
  }

  // Análises de áudio
  async getAudioAnalyses(
    page: number = 1,
    limit: number = 20,
    filters: AudioAnalysisFilters = {}
  ): Promise<AudioAnalysisListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value.toString();
        }
        return acc;
      }, {} as Record<string, string>),
    });

    const response = await this.api.get(`/audio-analyses?${params}`);
    return response.data;
  }

  async getAudioAnalysisById(id: number): Promise<ApiResponse<AudioAnalysis>> {
    const response = await this.api.get(`/audio-analyses/${id}`);
    return response.data;
  }

  async getStatistics(): Promise<StatisticsResponse> {
    const response = await this.api.get('/audio-analyses/stats');
    return response.data;
  }

  async getFilteredStatistics(filters: AudioAnalysisFilters = {}): Promise<ApiResponse> {
    const params = new URLSearchParams();
    
    // Adicionar filtros como parâmetros de query
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value.toString());
      }
    });
    
    const response = await this.api.get(`/audio-analyses/filtered-stats?${params.toString()}`);
    return response.data;
  }

  async generateDownloadToken(analysisId: number): Promise<ApiResponse> {
    const response = await this.api.post(`/audio-analyses/${analysisId}/download-token`);
    return response.data;
  }

  // Utilitários
  async healthCheck(): Promise<ApiResponse> {
    const response = await this.api.get('/health');
    return response.data;
  }

  // Método para download de arquivos
  getDownloadUrl(token: string): string {
    return `${API_BASE_URL}/download/${token}`;
  }

  // Método genérico para requisições customizadas
  async request<T = any>(config: AxiosRequestConfig): Promise<T> {
    const response = await this.api.request(config);
    return response.data;
  }
}

// Instância singleton da API
export const apiService = new ApiService();
export default apiService; 